import {
  DESIGN_PALETTE,
  MAX_SHAPES,
  STAGE_H,
  STAGE_W,
  timeoutPlaceholderDesign,
  type ClientRoomState,
  type DesignSubmission,
  type ShapeInstance,
  type ShapeType,
} from "@poppycock/shared";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useAutoSubmitOnTimeout } from "../hooks/useAutoSubmitOnTimeout";
import { StaticShape } from "./DesignStage";
import { PhaseTimer } from "./PhaseTimer";
import { Button, Card, PhaseBadge, ProgressDots } from "./ui";

const DEFAULT_COLOR = "#1C1C1E";

const SHAPE_TYPES: ShapeType[] = [
  "circle",
  "rect",
  "triangle",
  "roundRect",
  "line",
];

const SHAPE_LABELS: Record<ShapeType, string> = {
  circle: "Circle",
  rect: "Rect",
  triangle: "Triangle",
  roundRect: "Round",
  line: "Line",
};

const DEFAULT_SIZE: Record<ShapeType, { w: number; h: number }> = {
  circle: { w: 100, h: 100 },
  rect: { w: 140, h: 90 },
  triangle: { w: 110, h: 100 },
  roundRect: { w: 140, h: 90 },
  line: { w: 160, h: 14 },
};

const MIN_SIZE = 16;
const HANDLE = 18;
const ROTATE_OFFSET = 36;

function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function centerOf(s: ShapeInstance) {
  return { cx: s.x + s.w / 2, cy: s.y + s.h / 2 };
}

function nextZ(shapes: ShapeInstance[]): number {
  if (shapes.length === 0) return 0;
  return Math.max(...shapes.map((s) => s.z)) + 1;
}

function sortedByZ(shapes: ShapeInstance[]): ShapeInstance[] {
  return [...shapes].sort((a, b) => a.z - b.z);
}

type Corner = "nw" | "ne" | "sw" | "se";

type DragMode =
  | {
      kind: "move";
      id: string;
      originX: number;
      originY: number;
      startX: number;
      startY: number;
    }
  | {
      kind: "resize";
      id: string;
      corner: Corner;
      startX: number;
      startY: number;
      startW: number;
      startH: number;
      originX: number;
      originY: number;
      startRot: number;
    }
  | {
      kind: "rotate";
      id: string;
      cx: number;
      cy: number;
      startAngle: number;
      startRot: number;
    };

export function DesigningScreen({
  state,
  onSubmit,
}: {
  state: ClientRoomState;
  onSubmit: (design: DesignSubmission) => void;
}) {
  const round = state.round!;
  const submitted = round.youSubmitted;

  const [shapes, setShapes] = useState<ShapeInstance[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [defaultColor, setDefaultColor] = useState<string>(DEFAULT_COLOR);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragRef = useRef<DragMode | null>(null);
  const shapesRef = useRef(shapes);
  shapesRef.current = shapes;

  const forceSubmit = useCallback(() => {
    const current = shapesRef.current;
    if (current.length > 0) {
      onSubmit({ shapes: current });
    } else {
      onSubmit(timeoutPlaceholderDesign());
    }
  }, [onSubmit]);

  const countdown = useAutoSubmitOnTimeout(
    state.phaseEndsAt,
    submitted,
    forceSubmit,
  );

  const selected = useMemo(
    () => shapes.find((s) => s.id === selectedId) ?? null,
    [shapes, selectedId],
  );

  const atLimit = shapes.length >= MAX_SHAPES;

  const clientToSvg = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }, []);

  const updateShape = useCallback(
    (id: string, patch: Partial<ShapeInstance>) => {
      setShapes((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      );
    },
    [],
  );

  const addShape = useCallback(
    (type: ShapeType) => {
      if (shapesRef.current.length >= MAX_SHAPES) return;
      const size = DEFAULT_SIZE[type];
      const id = uid();
      const z = nextZ(shapesRef.current);
      const jitter = (shapesRef.current.length % 5) * 12;
      const shape: ShapeInstance = {
        id,
        type,
        x: STAGE_W / 2 - size.w / 2 + jitter,
        y: STAGE_H / 2 - size.h / 2 + jitter,
        w: size.w,
        h: size.h,
        rotation: 0,
        color: defaultColor,
        z,
      };
      setShapes((prev) => [...prev, shape]);
      setSelectedId(id);
    },
    [defaultColor],
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setShapes((prev) => prev.filter((s) => s.id !== selectedId));
    setSelectedId(null);
  }, [selectedId]);

  const bringForward = useCallback(() => {
    if (!selectedId) return;
    setShapes((prev) => {
      const ordered = sortedByZ(prev);
      const idx = ordered.findIndex((s) => s.id === selectedId);
      if (idx < 0 || idx >= ordered.length - 1) return prev;
      const a = ordered[idx]!;
      const b = ordered[idx + 1]!;
      return prev.map((s) => {
        if (s.id === a.id) return { ...s, z: b.z };
        if (s.id === b.id) return { ...s, z: a.z };
        return s;
      });
    });
  }, [selectedId]);

  const sendBackward = useCallback(() => {
    if (!selectedId) return;
    setShapes((prev) => {
      const ordered = sortedByZ(prev);
      const idx = ordered.findIndex((s) => s.id === selectedId);
      if (idx <= 0) return prev;
      const a = ordered[idx]!;
      const b = ordered[idx - 1]!;
      return prev.map((s) => {
        if (s.id === a.id) return { ...s, z: b.z };
        if (s.id === b.id) return { ...s, z: a.z };
        return s;
      });
    });
  }, [selectedId]);

  const setSelectedColor = useCallback(
    (color: string) => {
      setDefaultColor(color);
      if (selectedId) updateShape(selectedId, { color });
    },
    [selectedId, updateShape],
  );

  const handleSubmit = useCallback(() => {
    if (shapes.length === 0) return;
    onSubmit({ shapes: sortedByZ(shapes).map((s) => ({ ...s })) });
  }, [shapes, onSubmit]);

  useEffect(() => {
    if (submitted) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        deleteSelected();
      }
      if (e.key === "Escape") setSelectedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, deleteSelected, submitted]);

  useEffect(() => {
    if (submitted) return;
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      e.preventDefault();
      const pt = clientToSvg(e.clientX, e.clientY);

      if (drag.kind === "move") {
        updateShape(drag.id, {
          x: drag.startX + (pt.x - drag.originX),
          y: drag.startY + (pt.y - drag.originY),
        });
        return;
      }

      if (drag.kind === "rotate") {
        const angle =
          (Math.atan2(pt.y - drag.cy, pt.x - drag.cx) * 180) / Math.PI;
        updateShape(drag.id, {
          rotation: (((drag.startRot + (angle - drag.startAngle)) % 360) + 360) % 360,
        });
        return;
      }

      if (drag.kind === "resize") {
        const cx = drag.startX + drag.startW / 2;
        const cy = drag.startY + drag.startH / 2;
        const rad = (-drag.startRot * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const localX = (pt.x - cx) * cos - (pt.y - cy) * sin;
        const localY = (pt.x - cx) * sin + (pt.y - cy) * cos;

        const halfW = drag.startW / 2;
        const halfH = drag.startH / 2;
        let left = -halfW;
        let right = halfW;
        let top = -halfH;
        let bottom = halfH;

        switch (drag.corner) {
          case "nw":
            left = Math.min(localX, right - MIN_SIZE);
            top = Math.min(localY, bottom - MIN_SIZE);
            break;
          case "ne":
            right = Math.max(localX, left + MIN_SIZE);
            top = Math.min(localY, bottom - MIN_SIZE);
            break;
          case "sw":
            left = Math.min(localX, right - MIN_SIZE);
            bottom = Math.max(localY, top + MIN_SIZE);
            break;
          case "se":
            right = Math.max(localX, left + MIN_SIZE);
            bottom = Math.max(localY, top + MIN_SIZE);
            break;
        }

        const newW = right - left;
        const newH = bottom - top;
        const newLocalCx = (left + right) / 2;
        const newLocalCy = (top + bottom) / 2;
        const rad2 = (drag.startRot * Math.PI) / 180;
        const cos2 = Math.cos(rad2);
        const sin2 = Math.sin(rad2);
        const worldCx = cx + newLocalCx * cos2 - newLocalCy * sin2;
        const worldCy = cy + newLocalCx * sin2 + newLocalCy * cos2;

        updateShape(drag.id, {
          x: worldCx - newW / 2,
          y: worldCy - newH / 2,
          w: newW,
          h: newH,
        });
      }
    };

    const onUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [clientToSvg, updateShape, submitted]);

  const startMove = (shape: ShapeInstance, e: ReactPointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(shape.id);
    const pt = clientToSvg(e.clientX, e.clientY);
    dragRef.current = {
      kind: "move",
      id: shape.id,
      originX: pt.x,
      originY: pt.y,
      startX: shape.x,
      startY: shape.y,
    };
  };

  const startResize = (
    shape: ShapeInstance,
    corner: Corner,
    e: ReactPointerEvent,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const pt = clientToSvg(e.clientX, e.clientY);
    dragRef.current = {
      kind: "resize",
      id: shape.id,
      corner,
      startX: shape.x,
      startY: shape.y,
      startW: shape.w,
      startH: shape.h,
      originX: pt.x,
      originY: pt.y,
      startRot: shape.rotation,
    };
  };

  const startRotate = (shape: ShapeInstance, e: ReactPointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const { cx, cy } = centerOf(shape);
    const pt = clientToSvg(e.clientX, e.clientY);
    dragRef.current = {
      kind: "rotate",
      id: shape.id,
      cx,
      cy,
      startAngle: (Math.atan2(pt.y - cy, pt.x - cx) * 180) / Math.PI,
      startRot: shape.rotation,
    };
  };

  if (submitted) {
    return (
      <div className="flex flex-1 flex-col">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs tracking-wide text-[var(--color-muted)] uppercase">
              Round {round.number}
            </p>
            <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
              Design locked in
            </h2>
          </div>
          <PhaseBadge phase="writing" kind="design" />
        </header>
        <Card className="mb-6 flex-1">
          <div className="flex h-full flex-col items-center justify-center py-10 text-center">
            <p className="mb-2 text-lg font-semibold">Waiting on the slowpokes…</p>
            <p className="mb-6 text-sm text-[var(--color-muted)]">
              Your masterpiece is sealed.
            </p>
            <ProgressDots
              submitted={round.submittedCount}
              total={round.totalPlayers}
            />
          </div>
        </Card>
      </div>
    );
  }

  const ordered = sortedByZ(shapes);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs tracking-wide text-[var(--color-muted)] uppercase">
            Round {round.number}
          </p>
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
            Draw something unhinged
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            {shapes.length}/{MAX_SHAPES} shapes
            {countdown.urgency !== "none" ? " · ships at 0:00" : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PhaseTimer
            label={countdown.label}
            urgency={countdown.urgency}
            active={countdown.active}
          />
          <PhaseBadge phase="writing" kind="design" />
        </div>
      </header>

      <Card strong>
        <p className="mb-2 text-xs tracking-[0.18em] text-[var(--color-accent)] uppercase">
          Category
        </p>
        <p className="text-xl leading-snug font-semibold tracking-tight sm:text-2xl">
          {round.category}
        </p>
      </Card>

      <Card className="!p-3 sm:!p-4">
        <p className="mb-2 text-[11px] font-medium tracking-wide text-[var(--color-muted)] uppercase">
          Add shape
        </p>
        <div className="flex flex-wrap gap-2">
          {SHAPE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              disabled={atLimit}
              onClick={() => addShape(type)}
              className="inline-flex min-h-11 min-w-[4.5rem] flex-col items-center justify-center gap-1 rounded-xl border border-white/10 bg-black/25 px-3 py-2 text-xs font-semibold text-[var(--color-cream)] transition hover:border-[var(--color-accent)]/40 hover:bg-white/8 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ShapeTypeIcon type={type} />
              {SHAPE_LABELS[type]}
            </button>
          ))}
        </div>
        {atLimit && (
          <p className="mt-2 text-xs text-[var(--color-warn)]">
            Max {MAX_SHAPES} shapes — delete one to add more.
          </p>
        )}
      </Card>

      <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.35)]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${STAGE_W} ${STAGE_H}`}
          className="block h-auto w-full touch-none select-none"
          style={{ maxHeight: "min(52vh, 480px)" }}
          onPointerDown={() => setSelectedId(null)}
          role="application"
          aria-label="Design stage"
        >
          <rect x={0} y={0} width={STAGE_W} height={STAGE_H} fill="#F4F2EE" />
          <g opacity={0.06} pointerEvents="none">
            {Array.from({ length: 15 }, (_, i) => (
              <line
                key={`v${i}`}
                x1={(i + 1) * 50}
                y1={0}
                x2={(i + 1) * 50}
                y2={STAGE_H}
                stroke="#000"
                strokeWidth={1}
              />
            ))}
            {Array.from({ length: 11 }, (_, i) => (
              <line
                key={`h${i}`}
                x1={0}
                y1={(i + 1) * 50}
                x2={STAGE_W}
                y2={(i + 1) * 50}
                stroke="#000"
                strokeWidth={1}
              />
            ))}
          </g>

          {ordered.map((shape) => {
            const isSelected = shape.id === selectedId;
            const isHovered = shape.id === hoveredId && !isSelected;
            return (
              <g
                key={shape.id}
                onPointerEnter={() => setHoveredId(shape.id)}
                onPointerLeave={() =>
                  setHoveredId((h) => (h === shape.id ? null : h))
                }
              >
                {isHovered && (
                  <rect
                    x={shape.x - 2}
                    y={shape.y - 2}
                    width={shape.w + 4}
                    height={shape.h + 4}
                    fill="none"
                    stroke="#007AFF"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    opacity={0.55}
                    transform={`rotate(${shape.rotation} ${centerOf(shape).cx} ${centerOf(shape).cy})`}
                    pointerEvents="none"
                  />
                )}
                <g
                  onPointerDown={(e) => startMove(shape, e)}
                  style={{ cursor: "move" }}
                >
                  <StaticShape shape={shape} />
                  <rect
                    x={shape.x}
                    y={shape.y}
                    width={shape.w}
                    height={shape.h}
                    fill="transparent"
                    transform={`rotate(${shape.rotation} ${centerOf(shape).cx} ${centerOf(shape).cy})`}
                  />
                </g>
              </g>
            );
          })}

          {selected && (
            <SelectionChrome
              shape={selected}
              onResizeStart={startResize}
              onRotateStart={startRotate}
            />
          )}
        </svg>
      </div>

      <Card className="!p-3 sm:!p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="mb-2 text-[11px] font-medium tracking-wide text-[var(--color-muted)] uppercase">
              {selected ? "Color (selected)" : "Default color"}
            </p>
            <div className="flex flex-wrap gap-2">
              {DESIGN_PALETTE.map((c) => {
                const active = selected
                  ? selected.color === c
                  : defaultColor === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSelectedColor(c)}
                    title={c}
                    className="relative h-11 w-11 rounded-full border-2 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
                    style={{
                      backgroundColor: c,
                      borderColor: active
                        ? "var(--color-accent)"
                        : c === "#FFFFFF"
                          ? "rgba(255,255,255,0.35)"
                          : "rgba(255,255,255,0.12)",
                      boxShadow: active
                        ? "0 0 0 2px rgba(57,181,74,0.45)"
                        : undefined,
                    }}
                    aria-label={`Color ${c}`}
                    aria-pressed={active}
                  />
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={!selected}
              onClick={bringForward}
              className="min-h-11"
            >
              Forward
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!selected}
              onClick={sendBackward}
              className="min-h-11"
            >
              Backward
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={!selected}
              onClick={deleteSelected}
              className="min-h-11"
            >
              Delete
            </Button>
          </div>
        </div>
      </Card>

      <Button
        className="w-full"
        disabled={shapes.length === 0}
        onClick={handleSubmit}
      >
        Submit design
      </Button>
    </div>
  );
}

function SelectionChrome({
  shape,
  onResizeStart,
  onRotateStart,
}: {
  shape: ShapeInstance;
  onResizeStart: (
    shape: ShapeInstance,
    corner: Corner,
    e: ReactPointerEvent,
  ) => void;
  onRotateStart: (shape: ShapeInstance, e: ReactPointerEvent) => void;
}) {
  const { cx, cy } = centerOf(shape);
  const t = `rotate(${shape.rotation} ${cx} ${cy})`;
  const corners: { id: Corner; x: number; y: number; cursor: string }[] = [
    { id: "nw", x: shape.x, y: shape.y, cursor: "nwse-resize" },
    { id: "ne", x: shape.x + shape.w, y: shape.y, cursor: "nesw-resize" },
    { id: "sw", x: shape.x, y: shape.y + shape.h, cursor: "nesw-resize" },
    {
      id: "se",
      x: shape.x + shape.w,
      y: shape.y + shape.h,
      cursor: "nwse-resize",
    },
  ];
  const rotX = cx;
  const rotY = shape.y - ROTATE_OFFSET;

  return (
    <g pointerEvents="all">
      <rect
        x={shape.x}
        y={shape.y}
        width={shape.w}
        height={shape.h}
        fill="none"
        stroke="#007AFF"
        strokeWidth={2}
        transform={t}
        pointerEvents="none"
      />
      <line
        x1={cx}
        y1={shape.y}
        x2={rotX}
        y2={rotY}
        stroke="#007AFF"
        strokeWidth={1.5}
        transform={t}
        pointerEvents="none"
      />
      <circle
        cx={rotX}
        cy={rotY}
        r={HANDLE}
        fill="#007AFF"
        stroke="#fff"
        strokeWidth={2}
        transform={t}
        style={{ cursor: "grab" }}
        onPointerDown={(e) => onRotateStart(shape, e)}
      />
      {corners.map((c) => (
        <rect
          key={c.id}
          x={c.x - HANDLE / 2}
          y={c.y - HANDLE / 2}
          width={HANDLE}
          height={HANDLE}
          rx={2}
          fill="#fff"
          stroke="#007AFF"
          strokeWidth={2}
          transform={t}
          style={{ cursor: c.cursor }}
          onPointerDown={(e) => onResizeStart(shape, c.id, e)}
        />
      ))}
    </g>
  );
}

function ShapeTypeIcon({ type }: { type: ShapeType }) {
  const stroke = "currentColor";
  switch (type) {
    case "circle":
      return (
        <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
          <circle
            cx="11"
            cy="11"
            r="7"
            fill="none"
            stroke={stroke}
            strokeWidth="1.75"
          />
        </svg>
      );
    case "rect":
      return (
        <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
          <rect
            x="4"
            y="6"
            width="14"
            height="10"
            fill="none"
            stroke={stroke}
            strokeWidth="1.75"
          />
        </svg>
      );
    case "roundRect":
      return (
        <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
          <rect
            x="4"
            y="6"
            width="14"
            height="10"
            rx="3"
            fill="none"
            stroke={stroke}
            strokeWidth="1.75"
          />
        </svg>
      );
    case "triangle":
      return (
        <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
          <polygon
            points="11,4 18,17 4,17"
            fill="none"
            stroke={stroke}
            strokeWidth="1.75"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "line":
      return (
        <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
          <line
            x1="4"
            y1="15"
            x2="18"
            y2="7"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
  }
}

